import { observer } from "mobx-react";
import { memo } from "react";
import { Collapse } from "antd";
import style from "./style.module.scss";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";

const { Panel } = Collapse;
interface Props {}

interface Props {}
const Faqs: React.FC<Props> = observer(({ ...props }) => {
  const onChange = (key: string | string[]) => {};
  return (
    <div className={style.mainContainer}>
      <h2 style={{ fontSize: "36px", fontWeight: "500" }}>FAQs</h2>
      <div className={style.container}>
        <Collapse
          expandIcon={({ isActive }) =>
            isActive ? (
              <div className={style.plusIcon}>
                <MinusOutlined />
              </div>
            ) : (
              <div className={style.plusIcon}>
                <PlusOutlined />
              </div>
            )
          }
          defaultActiveKey={["1"]}
          onChange={onChange}
        >
          <Panel
            header="Q: Does the tool only review concept notes, or does it also help generate them?"
            key="1"
          >
            <p>
              A: Climate Finance Copilot does both. It reviews concept notes
              against known donor criteria and helps users generate concept
              notes that meet those criteria. This is achieved through expertly
              engineered prompts guiding commercial Large Language Models
              (LLMs). Beyond review and drafting, specialist "Assistants" within
              the tool provide targeted support for challenging areas like
              climate rationale and theory of change.
            </p>
          </Panel>

          <Panel
            header="Q: Does the software work with full funding proposals, or just concept notes?"
            key="2"
          >
            <p>
              A: While there aren't dedicated Assistants for drafting or
              reviewing full GCF proposals, the "Playground" feature (a GPT-like
              interface) can support proposal development and assessment through
              various techniques covered in user training.
            </p>
          </Panel>

          <Panel
            header="Q: For what phase of the project cycle is the tool best suited?"
            key="3"
          >
            <p>
              A: Climate Finance Co-pilot is valuable for all phases of the
              project cycle, especially for project research, Concept Note
              development, and compliance review. Whether starting with core
              information for a project idea or evaluating a full proposal, it
              ensures alignment with funders' requirements.
            </p>
          </Panel>

          <Panel
            header="Q: What kind of organization is this tool suited for?"
            key="4"
          >
            <p>
              A: The Climate Finance toolkit was designed for climate finance
              operations looking to boost their internal productivity by 3x –
              5x. This includes Accredited Entities, Nationally Designated
              Authorities, Project Proponents, etc. The software helps these
              organizations accelerate the production and review of funding
              packages and boost their overall quality prior to submission.
            </p>
          </Panel>

          <Panel header="Q: What informs the software's assessments?" key="5">
            <p>
              A: The assessment logic is based on established criteria from the
              GCF programmatic guide, Board decisions, and other guiding
              documents. It's further enhanced by insights from professional
              reviewers. This approach prioritizes objectivity, especially as
              the GCF continues standardizing its criteria.
            </p>
          </Panel>

          <Panel
            header="Q: Does the software maintain a database of successful examples?"
            key="6"
          >
            <p>
              A: No. GCF concept note scoring isn't currently standardized
              enough. Based on our analysis, some successful concept notes have
              deficiencies, and using these could negatively bias the tool.
            </p>
          </Panel>

          <Panel
            header="Q: Do you utilize machine learning to inform the software’s assessment logic?"
            key="7"
          >
            <p>
              A: Not currently. Effective machine learning requires a large,
              diverse dataset of concept notes with varying quality levels,
              which isn't available. Also, commercial LLMs have advanced
              significantly over the last 12 months negating the need for
              machine learning for many of the use cases we need to address.
            </p>
          </Panel>

          <Panel
            header="Q: Is there any background database the LLM accesses to support its output?"
            key="8"
          >
            <p>
              A: The base Copilot model, like the one used in our technical
              demos, doesn't use vector databases (Retrieval Augmented
              Generation - RAG). However, enterprise users can opt to
              incorporate RAG, and we'll support its setup. We advocate for RAG
              as it supplements the LLM's knowledge base.
            </p>
          </Panel>

          <Panel
            header="Q: How does the software know how to evaluate the Concept Notes and Proposals?"
            key="9"
          >
            <p>
              A: To inform Climate Finance Co-pilot’s processes, we primarily
              utilize the donor’s publicly available materials on how it
              evaluates funding applications.
            </p>
          </Panel>

          <Panel
            header="Q: How do you ensure the privacy and security of user data?"
            key="10"
          >
            <p>
              A: In contrast to systems like ChatGPT that store user prompts and
              queries, the LLMs we utilize are configured by default for
              non-retention of data and do not train on user-provided content.
              However, saved project content is stored in our database in
              encrypted format until permanently expunged from our systems via
              the delete function.
            </p>
          </Panel>

          <Panel
            header="Q: How can I be sure that my sensitive information is handled correctly?"
            key="11"
          >
            <p>
              A: We encourage users to avoid inputting highly sensitive personal
              information into the application. However, if it is necessary,
              rest assured that our system is designed to handle such data with
              the utmost care and security, in line with our stringent privacy
              policies and regulatory requirements.
            </p>
          </Panel>
        </Collapse>
      </div>
    </div>
  );
});

export default memo(Faqs);
